import React from "react";
import {useQueryClient} from "react-query";
import {useRecoilState, useRecoilValue, useSetRecoilState} from "recoil";
import {middleCategoryIdState} from "../../../Recoil/Atom/CategoryListAtom";
import * as categoryApi from "../../../api/educationApi/educationCategoryApi";
import * as categoryListApi from "../../../api/educationApi/educationCategoryApi";
import SubTitle from "../../../components/SubTitle";
import {useGetQuery, useRemoveMutation} from "../../../hooks/useQueryHooks";
import {ResponseList, ResponseRemove} from "../../../models/clientModels/interfaces/Response";
import * as Params from "../../../models/clientModels/interfaces/apiParams";
import {
  EDCategory,
  initialEDCategoryListParams,
} from "../../../models/serverModels/education/EDCategory";

import {
  Breadcrumbs,
  Button,
  Link as Linked,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {useNavigate, useParams} from "react-router-dom";
import {SubMenuGroup} from "../../../styledComponents/StyledListContainer";

import GrainIcon from "@mui/icons-material/Grain";
import HomeIcon from "@mui/icons-material/Home";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import {alertStateAtom} from "../../../Recoil/Atom/AlertAtom";
import {tokenSelector} from "../../../Recoil/Atom/authState";
import MWSkeleton from "../../../components/MWSkeleton";
import DataSaverOnIcon from "@mui/icons-material/DataSaverOn";
import {StyledNoneScrollTable, StyledNoneStickyTable} from "../../../styledComponents/StyledTable";

const SubCategoryList = () => {
  const queryClient = useQueryClient();

  let {param} = useParams();
  const depth = 2;
  const navigate = useNavigate();

  const tokenState = useRecoilValue(tokenSelector);

  const [middleCategoryId, setMiddleCategoryId] = useRecoilState(middleCategoryIdState);

  const setAlertState = useSetRecoilState(alertStateAtom);

  // region: Query (교육과정 카테고리 리스트)

  const {
    data: subCategory,
    isLoading,
    isFetching,
  } = useGetQuery<ResponseList<EDCategory>>({
    key: ["EducationCategoryList", param],
    api: () =>
      categoryListApi.getList({
        ...initialEDCategoryListParams,
        token: tokenState,
        depth: 2,
        parentUID: parseInt(param!),
      }),
  });

  const {mutate: removeMutate} = useRemoveMutation<ResponseRemove, Params.Remove>({
    api: categoryApi.remove,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries("EducationCategoryList");
      },
    },
  });

  return (
    <React.Fragment>
      <SubTitle subTitle="세 분류목록" />
      <SubMenuGroup>
        <div role="presentation">
          <Breadcrumbs aria-label="breadcrumb" sx={{paddingLeft: "8px"}}>
            <Linked
              sx={{display: "flex", alignItems: "center", cursor: "pointer"}}
              onClick={() => navigate(`/Education/CategoryList`)}
            >
              <HomeIcon sx={{mr: 0.5}} fontSize="inherit" />
              대분류
            </Linked>
            <Linked
              underline="hover"
              sx={{display: "flex", alignItems: "center", cursor: "pointer"}}
              onClick={() => navigate(`/Education/MiddleCategoryList/${middleCategoryId}`)}
            >
              <WhatshotIcon sx={{mr: 0.5}} fontSize="inherit" />
              중분류
            </Linked>
            <Typography sx={{display: "flex", alignItems: "center"}} color="text.primary">
              <GrainIcon sx={{mr: 0.5}} fontSize="inherit" />
              세분류
            </Typography>
          </Breadcrumbs>
        </div>
        <Button
          color="primary"
          variant="outlined"
          size="small"
          startIcon={<DataSaverOnIcon />}
          onClick={() => {
            navigate(`/Education/Create/${depth}/${param}`);
          }}
        >
          추가
        </Button>
      </SubMenuGroup>
      <StyledNoneScrollTable>
        <Table>
          <StyledNoneStickyTable>
            <TableRow>
              <TableCell align="center">#</TableCell>
              <TableCell align="center">이름</TableCell>
              <TableCell align="center">분류</TableCell>
              <TableCell align="center">편집</TableCell>
            </TableRow>
          </StyledNoneStickyTable>
          <TableBody>
            {isLoading && isFetching ? (
              <MWSkeleton row={12} column={4} />
            ) : (
              subCategory?.items.map((item, index) => (
                <TableRow key={item.uid}>
                  <TableCell align="center">{index + 1}</TableCell>
                  <TableCell align="center">{item.name}</TableCell>
                  <TableCell align="center">하위분류없음</TableCell>
                  <TableCell align="center">
                    <Button
                      color="primary"
                      variant="outlined"
                      size="small"
                      sx={{marginRight: "16px"}}
                      onClick={() => {
                        navigate(`/Education/Update/${depth}/${param}`, {state: item.uid});
                      }}
                    >
                      편집
                    </Button>
                    <Button
                      color="error"
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setAlertState({
                          toggle: true,
                          message: "세분류를 삭제하시겠습니까?",
                          execution: () => removeMutate({token: tokenState, id: item.uid}),
                        });
                      }}
                    >
                      삭제
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </StyledNoneScrollTable>
    </React.Fragment>
  );
};

export default SubCategoryList;
